import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"

import { clientApi, projectApi } from "../../api"
import { usePermissions } from "../../permissions"
import ClientSelect from "../../components/ClientSelect"

/**
 * The same project, for another client.
 *
 * ── What this replaces ──────────────────────────────────────────────────────
 *
 * An onboarding that worked, opened in one tab and retyped into another. That
 * is an afternoon, and the step somebody forgets is always the one that
 * mattered. The tasks come across with their sub-tasks intact, so the checklist
 * stays a checklist rather than arriving as eleven loose tasks.
 *
 * ── Why the code is offered rather than generated silently ──────────────────
 *
 * A project's code is the prefix of every task reference inside it, so it is
 * the part people will be reading for the next year. Codes are unique per
 * client, so copying "ONB" into a client that already has one becomes "ONB2" —
 * which is fine, and worth saying out loud rather than discovering in a task
 * code later.
 */
export default function CopyProjectDialog({ project, onClose, onCopied }) {
  const navigate = useNavigate()
  const { can, isBackOffice } = usePermissions()

  const [clients, setClients] = useState([])
  const [clientId, setClientId] = useState("")
  const [name, setName] = useState("")
  const [code, setCode] = useState("")
  const [includeTasks, setIncludeTasks] = useState(true)
  const [keepStatuses, setKeepStatuses] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!project) return
    setClientId(String(project.client_id))
    setName(`${project.name} (copy)`)
    setCode(project.code || "")
    setIncludeTasks(true)
    setKeepStatuses(false)
    setError(null)

    if (isBackOffice && can("clients", "view")) {
      clientApi
        .list()
        .then((payload) => setClients(payload.organizations || []))
        .catch(() => setClients([]))
    }
  }, [project, isBackOffice, can])

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await projectApi.copy(project.id, {
        client_id: clientId,
        name: name.trim() || undefined,
        code: code.trim() || undefined,
        include_tasks: includeTasks,
        keep_statuses: keepStatuses,
      })
      onCopied?.(
        `Copied as ${result.project.code} — ${result.tasks} task${result.tasks === 1 ? "" : "s"}` +
          (result.subtasks ? ` and ${result.subtasks} step${result.subtasks === 1 ? "" : "s"}` : "") +
          ".",
      )
      navigate(`/projects/${result.project.id}`)
    } catch (err) {
      setError(err.userMessage || "That project could not be copied.")
    } finally {
      setBusy(false)
    }
  }

  const crossingClients = project && String(clientId) !== String(project.client_id)

  return (
    <Dialog open={Boolean(project)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>
          Duplicate “{project?.name}”
          <Typography variant="caption" color="text.secondary" display="block">
            A new project with the same shape of work. The original is untouched.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Stack spacing={2}>
            {isBackOffice && clients.length > 0 ? (
              <ClientSelect
                clients={clients}
                value={clientId}
                onChange={setClientId}
                label="For which client"
                fullWidth
              />
            ) : null}

            <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} fullWidth autoFocus />

            <TextField
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              fullWidth
              helperText="The prefix on every task in it. Taken as close to this as is free."
            />

            <Stack>
              <FormControlLabel
                control={<Switch checked={includeTasks} onChange={(event) => setIncludeTasks(event.target.checked)} />}
                label="Bring the tasks and their steps across"
              />
              <FormControlLabel
                control={<Switch checked={keepStatuses} onChange={(event) => setKeepStatuses(event.target.checked)} />}
                label="Keep where each one had got to"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 5.5, mt: -0.5 }}>
                Off, everything starts at the beginning — which is what reusing a project usually means.
              </Typography>
            </Stack>

            <Alert severity="info">
              Comments, attachments and logged time stay with the original project.
              {crossingClients
                ? " So do the people: assignees are not carried into another organization, and membership is set there."
                : ""}
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || !clientId || !name.trim()}>
            {busy ? "Copying…" : "Duplicate it"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
